import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { ApiError, NetworkError } from '@/api/client';
import { useT } from '@/i18n/LanguageContext';
import { LanguageToggle } from '@/i18n/LanguageToggle';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). A real email+password login form
 * - the seller portal's own auth boundary (packages/operations/src/
 * services/sellerAuth.ts) supports exactly this, unlike apps/ops's
 * bearer-token-paste screen. The backend's login handler is deliberately
 * generic on failure (PORTAL_AUTH.INVALID_CREDENTIALS for both an unknown
 * email and a wrong password, constant-shape - ADR-075 §Part B.2), so this
 * screen never tries to distinguish the two either.
 */
export function LoginPage(): JSX.Element {
  const { state, login } = useAuth();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell card">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LanguageToggle />
      </div>
      <h1 style={{ fontSize: 18, marginTop: 0 }}>{t('login.title')}</h1>
      <p className="sub" style={{ marginTop: -8 }}>
        {t('login.subtitle')}
      </p>
      <form onSubmit={onSubmit}>
        <div className="field-row">
          <label htmlFor="email">{t('login.email')}</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field-row">
          <label htmlFor="password">{t('login.password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-describedby={error ? 'login-error' : undefined}
          />
        </div>
        {Boolean(error) && (
          <div id="login-error" className="error-banner" role="alert">
            {describeLoginError(t, error)}
          </div>
        )}
        <div className="form-actions">
          <button type="submit" className="primary" disabled={submitting || !email.trim() || !password}>
            {submitting ? t('login.signingIn') : t('login.signIn')}
          </button>
        </div>
      </form>
      <p className="sub" style={{ marginTop: 16 }}>
        <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
      </p>
    </div>
  );
}

function describeLoginError(t: ReturnType<typeof useT>, error: unknown): string {
  if (error instanceof ApiError && error.status === 401) {
    return t('login.incorrectCredentials');
  }
  // RATE-01: too many attempts from this network - wait, do not retype the password.
  if (error instanceof ApiError && error.status === 429) return t('error.tooManyRequests');
  if (error instanceof NetworkError) return error.message;
  return t('login.couldNotSignIn');
}
